import React, { useEffect, useMemo, useState } from 'react';
import { Save, AlertTriangle } from 'lucide-react';
import axiosInstance from '../utils/axiosInstance';
import AppShell from '../components/AppShell';

// Operator-facing settings, grouped. Keys not listed here still appear
// under "Advanced" so nothing is hidden as new settings are added.
const GROUPS = [
  {
    label: 'Posting to Gorgias',
    hint: 'When and how confidently Replai posts drafts as internal notes.',
    keys: [
      'auto_note_enabled',
      'auto_note_min_confidence',
      'auto_note_min_confidence_per_category',
      'auto_note_low_confidence_enabled',
      'auto_note_low_floor',
      'auto_note_verifier_enabled',
      'auto_note_suppress_no_answer',
      'auto_note_suppress_noise_playbooks',
      'overdue_escalation_days',
    ],
  },
  {
    label: 'Visual cards',
    hint: 'Attach the branded order-status / stock-proof images to notes.',
    keys: ['auto_note_attach_status_card', 'auto_note_attach_stock_proof'],
  },
  {
    label: 'Pipeline & ingest',
    hint: 'What runs automatically and which Gorgias views are ingested.',
    keys: ['auto_dispatch_enabled', 'agentic_endpoints_enabled', 'ingest_view_ids'],
  },
  {
    label: 'Assignment & hygiene',
    keys: ['auto_assign_enabled', 'auto_fix_customer_enabled', 'excluded_senders'],
  },
  {
    label: 'Other',
    keys: ['feedback_slack_channel', 'partial_ship_min_final_margin_pct', 'business_facts'],
  },
];

const META = {
  auto_note_enabled: { label: 'Auto-post notes (master switch)', help: 'Post confident drafts to Gorgias as agent-only internal notes.' },
  auto_note_min_confidence: { label: 'High-band confidence threshold', help: 'Drafts at or above this post clean; below get the ⚠️ banner.' },
  auto_note_min_confidence_per_category: { label: 'Per-category thresholds', help: 'Overrides the global threshold for specific categories (JSON).' },
  auto_note_low_confidence_enabled: { label: 'Post low-confidence with banner', help: 'Post drafts between the floor and threshold, flagged for a human glance.' },
  auto_note_low_floor: { label: 'Low-confidence floor', help: 'Below this confidence nothing posts at all.' },
  auto_note_verifier_enabled: { label: 'Verifier fact-check before posting', help: 'A second model checks high-band drafts and can demote a bad one.' },
  auto_note_suppress_no_answer: { label: 'Suppress empty deferrals', help: 'Do not post a note whose only content is "a colleague will follow up".' },
  auto_note_suppress_noise_playbooks: { label: 'Suppress noise playbooks', help: 'Skip agent notes on carrier/supplier auto-replies and our own outgoing mail.' },
  overdue_escalation_days: { label: 'Overdue escalation (days)', help: 'Escalate to a human only when overdue by more than this many days.' },
  auto_note_attach_status_card: { label: 'Attach order-status card', help: 'The "where\'s my order" image on order_status / tracking notes.' },
  auto_note_attach_stock_proof: { label: 'Attach stock-at-purchase proof', help: 'The proof image on stock-dispute tickets.' },
  auto_dispatch_enabled: { label: 'Auto-dispatch on ingest', help: 'Run classify → draft automatically as tickets are ingested.' },
  agentic_endpoints_enabled: { label: 'Agentic endpoint selection', help: 'Let the model pick which data endpoints to call (tool use).' },
  ingest_view_ids: { label: 'Gorgias view IDs to ingest', help: 'One numeric view id per line.' },
  auto_assign_enabled: { label: 'Auto-assign tickets', help: 'Assign incoming tickets to an agent based on customer history.' },
  auto_fix_customer_enabled: { label: 'Auto-fix customer attribution', help: 'Rebind placeholder/forwarded tickets to the real customer.' },
  excluded_senders: { label: 'Excluded senders', help: 'Emails/domains auto-archived at ingest. One per line.' },
  feedback_slack_channel: { label: 'Feedback Slack channel', help: 'Referenced in the note footer.' },
  partial_ship_min_final_margin_pct: { label: 'Partial-ship min margin', help: 'Minimum final margin fraction to allow a split shipment.' },
  business_facts: { label: 'Business facts (legacy)', help: 'Superseded by Knowledge cards; kept as backup. One per line.' },
};

const valueType = (v) => {
  if (typeof v === 'boolean') return 'bool';
  if (typeof v === 'number') return 'number';
  if (Array.isArray(v)) return 'list';
  if (v && typeof v === 'object') return 'json';
  return 'string';
};

const toEditable = (v) => {
  const t = valueType(v);
  if (t === 'list') return (v || []).join('\n');
  if (t === 'json') return JSON.stringify(v, null, 2);
  return v;
};

const fromEditable = (raw, original) => {
  const t = valueType(original);
  if (t === 'bool') return !!raw;
  if (t === 'number') return raw === '' ? original : Number(raw);
  if (t === 'list') {
    const items = String(raw).split('\n').map((s) => s.trim()).filter(Boolean);
    // preserve numeric lists (e.g. view ids)
    return (original || []).every((x) => typeof x === 'number')
      ? items.map(Number).filter((n) => !Number.isNaN(n))
      : items;
  }
  if (t === 'json') {
    try { return JSON.parse(raw); } catch { return original; }
  }
  return raw;
};

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition ${
      checked ? 'bg-emerald-600' : 'bg-[hsl(var(--secondary))]'
    }`}
  >
    <span className={`inline-block h-4 w-4 rounded-full bg-white transition ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

export default function Settings() {
  const [settings, setSettings] = useState(null);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.get('api/replai/settings/');
      setSettings(data);
      setEdits({});
    } catch (e) {
      setError('Could not load settings.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setEdit = (key, raw) => setEdits((p) => ({ ...p, [key]: raw }));

  const dirtyKeys = useMemo(() => {
    if (!settings) return [];
    return Object.keys(edits).filter((k) => {
      const next = fromEditable(edits[k], settings[k]);
      return JSON.stringify(next) !== JSON.stringify(settings[k]);
    });
  }, [edits, settings]);

  const save = async () => {
    if (!dirtyKeys.length) return;
    setSaving(true);
    setError('');
    try {
      const payload = {};
      dirtyKeys.forEach((k) => { payload[k] = fromEditable(edits[k], settings[k]); });
      const { data } = await axiosInstance.patch('api/replai/settings/', payload);
      setSettings(data.all || { ...settings, ...payload });
      setEdits({});
      setSavedAt(new Date());
    } catch (e) {
      setError('Save failed - check the values (JSON fields must be valid).');
    } finally {
      setSaving(false);
    }
  };

  const known = new Set(GROUPS.flatMap((g) => g.keys));
  const advanced = settings ? Object.keys(settings).filter((k) => !known.has(k)).sort() : [];

  const renderRow = (key) => {
    if (!settings || !(key in settings)) return null;
    const original = settings[key];
    const t = valueType(original);
    const raw = key in edits ? edits[key] : toEditable(original);
    const meta = META[key] || { label: key };
    const isDirty = dirtyKeys.includes(key);
    return (
      <div key={key} className={`flex items-start justify-between gap-4 py-3 border-b border-border ${isDirty ? 'bg-amber-50/40' : ''}`}>
        <div className="min-w-0">
          <p className="text-sm font-medium">{meta.label} {isDirty && <span className="text-amber-600 text-xs">• unsaved</span>}</p>
          {meta.help && <p className="text-xs text-muted-foreground mt-0.5">{meta.help}</p>}
          <code className="text-[0.65rem] text-muted-foreground/70">{key}</code>
        </div>
        <div className="shrink-0 w-64 flex justify-end">
          {t === 'bool' && <Toggle checked={!!raw} onChange={(v) => setEdit(key, v)} />}
          {t === 'number' && (
            <input type="number" step="any" value={raw} onChange={(e) => setEdit(key, e.target.value)}
              className="w-28 rounded-lg border border-border px-2 py-1 text-sm text-right" />
          )}
          {t === 'string' && (
            <input value={raw} onChange={(e) => setEdit(key, e.target.value)}
              className="w-full rounded-lg border border-border px-2 py-1 text-sm" />
          )}
          {(t === 'list' || t === 'json') && (
            <textarea value={raw} onChange={(e) => setEdit(key, e.target.value)} rows={t === 'json' ? 4 : 3}
              className="w-full rounded-lg border border-border px-2 py-1 text-xs font-mono" />
          )}
        </div>
      </div>
    );
  };

  return (
    <AppShell title="Settings" subtitle="Runtime configuration for the pipeline. Changes take effect within ~30s (settings cache TTL).">
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-background z-10 py-2">
        <div className="text-sm text-muted-foreground">
          {dirtyKeys.length ? `${dirtyKeys.length} unsaved change${dirtyKeys.length > 1 ? 's' : ''}` :
            savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : 'All changes saved'}
        </div>
        <button onClick={save} disabled={!dirtyKeys.length || saving}
          className="inline-flex items-center gap-1.5 rounded-xl bg-foreground px-3 py-2 text-sm font-medium text-white disabled:opacity-40">
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 text-red-700 px-3 py-2 text-sm">
          <AlertTriangle className="h-4 w-4" /> {error}
          <button onClick={load} className="ml-auto underline">Reload</button>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-6">
          {GROUPS.map((g) => (
            <section key={g.label} className="rounded-xl border border-border p-4">
              <h2 className="text-sm font-semibold">{g.label}</h2>
              {g.hint && <p className="text-xs text-muted-foreground mb-2">{g.hint}</p>}
              <div>{g.keys.map(renderRow)}</div>
            </section>
          ))}
          {advanced.length > 0 && (
            <section className="rounded-xl border border-border p-4">
              <h2 className="text-sm font-semibold">Advanced</h2>
              <p className="text-xs text-muted-foreground mb-2">Other settings not in a group above.</p>
              <div>{advanced.map(renderRow)}</div>
            </section>
          )}
        </div>
      )}
    </AppShell>
  );
}
